/* Muyva bundle offer - auto-applies the bundle discount code in the cart (preview theme only). */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const SRC = 'muyva-bundle';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const cfg = document.getElementById('muyva-bundle-config');
const list = (k) => new Set(((cfg && cfg.dataset[k]) || '').split(',').map((h) => h.trim()).filter(Boolean));
// One code per group, highest tier first. Groups never share products, so codes from different groups can coexist.
const GROUPS = [
  { handles: list('handles'), tiers: [[3, 'MUYBNDL3X15'], [2, 'MUYBNDL2X10']] },
  { handles: list('outdoor'), tiers: [[8, 'MUYLGT8X15'], [4, 'MUYLGT4X10'], [2, 'MUYLGT2X5']] },
  { handles: list('sconce'), tiers: [[4, 'MUYSCN4X12'], [2, 'MUYSCN2X10']] },
  { handles: list('marble'), tiers: [[4, 'MUYMBL4X10'], [2, 'MUYMBL2X5']] },
  { handles: list('bollard'), tiers: [[8, 'MUYBOL8X15'], [4, 'MUYBOL4X10'], [2, 'MUYBOL2X5']] },
  { handles: list('pendant'), tiers: [[3, 'MUYPND3X10'], [2, 'MUYPND2X5']] },
];
const OURS = GROUPS.flatMap((g) => g.tiers.map((t) => t[1]));
const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?$/;
const nativeFetch = window.fetch.bind(window);

let inflight = null;   // promise of the current swap
let pending = false;   // a theme cart write is in progress
let lastCart = null;   // newest full cart JSON we have seen
const swaps = [];

/** What our codes should look like for this cart. Returns null if nothing to change. */
function plan(cart) {
  const wantedList = [];
  for (const g of GROUPS) {
    const qty = (cart.items || []).reduce((n, i) => (g.handles.has(i.handle) ? n + i.quantity : n), 0);
    const tier = g.tiers.find((t) => qty >= t[0]);
    if (tier) wantedList.push(tier[1]);
  }
  const existing = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
  const others = existing.filter((c) => !OURS.includes(c.toUpperCase()));
  const ours = existing.filter((c) => OURS.includes(c.toUpperCase())).map((c) => c.toUpperCase());
  const ok = ours.length === wantedList.length && wantedList.every((c) => ours.includes(c));
  return ok ? null : [...others, ...wantedList].join(',');
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id], cart-discount-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

async function swap(discount) {
  const now = Date.now();
  while (swaps.length && now - swaps[0] > 15000) swaps.shift();
  if (swaps.length >= 6) return; // loop guard
  swaps.push(now);

  const ids = sectionIds();
  const body = { discount };
  if (ids.length) { body.sections = ids.join(','); body.sections_url = location.pathname; }
  const res = await nativeFetch(root + 'cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const cart = await res.json();
  lastCart = cart;
  const sections = cart.sections || {};

  // cart-items components (cart page + drawer) morph from these sections; cart-icon updates the count badge.
  document.dispatchEvent(new CartUpdateEvent(cart, SRC, { source: SRC, itemCount: cart.item_count, sections }));

  // Morph any other cart section (e.g. discount form) not owned by a cart-items component.
  const itemsIds = new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((e) => e.dataset.sectionId));
  for (const id of ids) if (!itemsIds.has(id) && sections[id]) { try { morphSection(id, sections[id]); } catch (e) {} }

  if (!ids.length && /\/cart\/?$/.test(location.pathname)) location.reload();
}

/** Check a cart (or fetch one) and swap immediately if needed. Serialised; newest cart wins. */
function check(cart) {
  if (cart) lastCart = cart;
  const run = async () => {
    let c = lastCart;
    if (!c) c = lastCart = await (await nativeFetch(root + 'cart.js', { cache: 'no-store' })).json();
    const d = plan(c);
    if (d !== null) await swap(d);
  };
  inflight = (inflight || Promise.resolve()).then(run, run).catch((e) => console.warn('[muyva-bundle]', e));
  const mine = inflight;
  mine.finally(() => { if (inflight === mine) inflight = null; });
  return mine;
}

// Intercept theme cart writes and react to the response JSON directly (it contains the full cart for change/update).
window.fetch = function (input, init) {
  let isWrite = false;
  try {
    const url = typeof input === 'string' ? input : input && input.url;
    isWrite = !!url && CART_WRITE.test(new URL(url, location.href).pathname);
  } catch (e) {}
  const p = nativeFetch(input, init);
  if (!isWrite) return p;
  pending = true;
  p.then((res) => res.clone().json())
    .then((json) => {
      pending = false;
      // change.js / update.js return the full cart (has items + item_count); add.js returns only the added items.
      if (json && Array.isArray(json.items) && typeof json.item_count === 'number') {
        lastCart = json;
        check(json);
      } else {
        lastCart = null;
        check(null);
      }
    })
    .catch(() => { pending = false; lastCart = null; check(null); });
  return p;
};

// Non-fetch cart changes (e.g. other scripts) still arrive as theme events.
document.addEventListener(ThemeEvents.cartUpdate, (e) => {
  if (e && e.detail && e.detail.sourceId === SRC) return;
  if (!pending && !inflight) { lastCart = null; check(null); }
});

// Checkout safety net: make sure the right code is on the cart before leaving (max ~3s wait).
async function ensureBeforeCheckout() {
  const t0 = Date.now();
  while (pending && Date.now() - t0 < 1500) await new Promise((r) => setTimeout(r, 100));
  lastCart = null;
  await Promise.race([check(null), new Promise((r) => setTimeout(r, 3000 - (Date.now() - t0)))]);
}
let proceeding = false;
function isCheckout(el) {
  if (!el || !el.closest) return null;
  const b = el.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"], #checkout, .cart__checkout-button');
  return b && !b.closest('.muyva-bundle') ? b : null;
}
document.addEventListener('click', (e) => {
  if (proceeding) return;
  const b = isCheckout(e.target);
  if (!b) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  ensureBeforeCheckout().finally(() => { proceeding = true; location.href = root + 'checkout'; });
}, true);
document.addEventListener('submit', (e) => {
  if (proceeding) return;
  const f = e.target;
  if (!(f instanceof HTMLFormElement)) return;
  const action = f.getAttribute('action') || '';
  const viaCheckout = e.submitter && e.submitter.name === 'checkout';
  if (!/\/cart\/?$/.test(new URL(action || location.href, location.href).pathname) && !viaCheckout) return;
  if (!viaCheckout && !f.querySelector('[name="checkout"]')) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  ensureBeforeCheckout().finally(() => { proceeding = true; location.href = root + 'checkout'; });
}, true);

check(null);
